import { useState, useEffect, useRef } from 'react'
import { extractPageContext } from '../../utils/pageContext'
import {
  type ConversationMessage,
  type AskRequest,
  type AskResponse,
  createConversationMessage,
  addToConversationHistory,
} from '../../types/conversation'

export function AskAnythingWidget() {
  const [inputValue, setInputValue] = useState('')
  const [answer, setAnswer] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const responsesRef = useRef<HTMLDivElement>(null)

  const showAnswer =
    answer.length > 0 || isLoading || error.length > 0 || conversationHistory.length > 0

  // Widget should be expanded if: hovering, focused, has value, or showing answer
  const shouldExpand = isExpanded || isFocused || inputValue.length > 0 || showAnswer

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return

    const question = inputValue.trim()
    setIsLoading(true)
    setError('')
    setAnswer('')

    try {
      // Extract page context for AI
      const pageContext = extractPageContext()

      // Add user message to conversation history and get updated history
      const userMessage = createConversationMessage('user', question)
      const updatedHistory = addToConversationHistory(conversationHistory, userMessage)

      // Update state with the new history
      setConversationHistory(updatedHistory)

      // Prepare request with the UPDATED conversation history (including current question)
      const requestData: AskRequest = {
        question,
        pageContext,
        conversationHistory: updatedHistory,
      }

      console.log(
        'Sending conversation history to API:',
        updatedHistory.map((msg) => ({
          role: msg.role,
          content: msg.content.substring(0, 50) + '...',
        }))
      )

      // Call API endpoint (try localhost first for development)
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/ask' : '/api/ask'
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: AskResponse = await response.json()

      if (data.error) {
        setError(data.error)
      } else {
        setAnswer(data.answer)

        // Add assistant response to conversation history
        const assistantMessage = createConversationMessage('assistant', data.answer)
        const finalHistory = addToConversationHistory(updatedHistory, assistantMessage)
        setConversationHistory(finalHistory)

        console.log(
          'Updated conversation after AI response:',
          finalHistory.map((msg) => ({
            role: msg.role,
            content: msg.content.substring(0, 50) + '...',
          }))
        )
      }
    } catch (err) {
      console.error('Ask API Error:', err)
      const errorMessage =
        'Sorry, I encountered an error while processing your question. Please try again.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
      setInputValue('') // Clear input after submission
    }
  }

  // Function to clear conversation history
  const clearConversation = () => {
    setConversationHistory([])
    setAnswer('')
    setError('')
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Clear all conversation state when clicking outside
        clearConversation()
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Auto-scroll to bottom when new responses are added
  useEffect(() => {
    if (responsesRef.current) {
      responsesRef.current.scrollTo({
        top: responsesRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [conversationHistory])

  return (
    <div
      className="ask-anything-container"
      ref={containerRef}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {showAnswer && (
        <div className="ask-anything-answer">
          <div className="answer-content">
            {/* AI Response History */}
            {conversationHistory.length > 0 && !isLoading && (
              <div className="ai-responses" ref={responsesRef}>
                {conversationHistory
                  .filter((msg) => msg.role === 'assistant')
                  .slice(-3)
                  .map((msg) => {
                    // Find the user question that preceded this response
                    const msgIndex = conversationHistory.findIndex((m) => m.id === msg.id)
                    const precedingQuestion =
                      msgIndex > 0 ? conversationHistory[msgIndex - 1] : null

                    return (
                      <div key={msg.id} className="ai-response-item">
                        {precedingQuestion && (
                          <div className="response-question">{precedingQuestion.content}</div>
                        )}
                        <div className="response-content">{msg.content}</div>
                      </div>
                    )
                  })}
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="loading-state">
                <div className="thinking-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="loading-text">Thinking...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="error-state">
                <span className="error-icon">⚠️</span>
                <span className="error-text">{error}</span>
              </div>
            )}

            {/* Current Answer (if not in conversation view) */}
            {answer && !isLoading && conversationHistory.length === 0 && (
              <div className="answer-text">{answer}</div>
            )}

            {/* Conversation Controls */}
            {conversationHistory.length > 0 && (
              <div className="conversation-controls">
                <button
                  onClick={clearConversation}
                  className="clear-conversation-btn"
                  title="Clear conversation"
                >
                  🗑️ Clear
                </button>
                <span className="conversation-count">
                  {conversationHistory.filter((msg) => msg.role === 'assistant').length} responses
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="ask-anything-form">
        <input
          type="text"
          className={`ask-anything-widget ${shouldExpand ? 'expanded' : 'collapsed'}`}
          placeholder={shouldExpand ? 'Ask Anything' : 'Ask'}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isLoading}
        />
        {shouldExpand && (
          <button
            type="submit"
            className="ask-submit-btn"
            disabled={!inputValue.trim() || isLoading}
          >
            {isLoading ? '⏳' : '🔍'}
          </button>
        )}
      </form>
    </div>
  )
}
